/**
 * ProductionMonitor.jsx  v2.0
 * Full integration with Nirvikalp ERP
 *
 * Data fetched automatically (no re-entry):
 *   Factories / Lines  ← db.suppliers (Sourcing module)
 *   Styles             ← db.mgmtStyles (Style Registry)
 *   Operations + SMV   ← style.workstationData.aggregatedOps (Workstation)
 *   Colors + Sizes     ← db.poSizeTable / style.pos (Style Registry / PO upload)
 *   Size-wise qty      ← db.poSizeTable rows
 *   Total qty          ← style.pos[].qty
 *
 * New data (highlighted in amber — only entered once here):
 *   Available time setup (shifts, breaks)
 *   Workers per line (if not in Nirvikalp yet)
 *   Lay details (lay No, plies, bundle size)
 *   Scan records
 *
 * API endpoint: /api/pm_api.php (see pm_api.php)
 * Fallback: localStorage when API unavailable
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Barcode, QRCode, BarcodeCard } from './BarcodeRenderer';
import ScanModule from './ScanModule';
import LeanManagement from './LeanManagement';

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const PM_API  = 'https://nirvikalp.integrashion.com/api/pm_api.php';
const NIRV_API = 'https://nirvikalp.integrashion.com/api';

// ─── NIRVIKALP DATA BRIDGE ────────────────────────────────────────────────────
// All helpers mirror ProductionPlanning.js logic exactly — no duplication.
const bridge = {
  /** Factories from Sourcing module */
  getFactories: (db) =>
    (db?.suppliers || []).filter(s => s.supplierType === 'Garment Supplier'),

  /** Lines for a factory (all field-name variants) */
  getLines: (factory) =>
    factory?.sewingLines || factory?.lines || factory?.productionLines || factory?.sewLines || [],

  /** All factories + lines flattened */
  getAllLines: (db) => {
    const lines = [];
    bridge.getFactories(db).forEach(f =>
      bridge.getLines(f).forEach(l =>
        lines.push({ ...l, factoryName: f.name, factoryId: f.id, factoryObj: f })
      )
    );
    return lines;
  },

  /** Full style data — mirrors getStyleData() from ProductionPlanning.js */
  getStyleData: (styleId, db) => {
    const s = (db?.mgmtStyles || []).find(st => st.id === styleId);
    if (!s) return null;
    const styleRef = s.styleRef || s.name || s.id;
    const rawPos = s.pos || s.purchaseOrders || s.orders || s.workstationData?.pos || [];
    const poTableRows = (db?.poSizeTable || []).filter(r => r.style_ref === styleRef);

    const pos = rawPos.map(p => ({
      num:  p.num || p.po_no || p.poNo || '-',
      qty:  parseFloat(p.qty || p.quantity || p.total_qty || 0) || 0,
      col:  p.col || p.color || p.colour || '',
      date: p.date || p.ex_fty_date || '',
      sizeBreakdown: p.sizeBreakdown || p.size_breakdown || '',
    })).filter(p => p.num !== '-');

    // Colors — union of POs + poSizeTable
    const colors = [...new Set([
      ...pos.map(p => p.col).filter(Boolean),
      ...poTableRows.map(r => r.color).filter(Boolean),
    ])];

    // Sizes — union of sizeBreakdowns + poSizeTable
    const sizesFromBreakdown = pos.flatMap(p => {
      const sb = p.sizeBreakdown;
      if (typeof sb === 'string' && sb.trim())
        return sb.split(',').map(s => s.split(':')[0].trim()).filter(Boolean);
      if (sb && typeof sb === 'object') return Object.keys(sb);
      return [];
    });
    const sizesFromTable = poTableRows.map(r => r.size).filter(Boolean);
    const sizes = [...new Set([...sizesFromTable, ...sizesFromBreakdown])];

    // Size × Color × Qty matrix from poSizeTable
    const sizeColorMatrix = poTableRows.map(r => ({
      poNo: r.po_no, color: r.color, size: r.size, qty: parseInt(r.qty) || 0,
    }));

    // Total qty
    const totalQty = parseFloat(s.totalQty) > 0
      ? parseFloat(s.totalQty)
      : pos.reduce((a, p) => a + (p.qty || 0), 0);

    // Operations + SMV from Workstation module
    const ops = s.workstationData?.aggregatedOps || [];
    const totalSMV = parseFloat(s.workstationData?.smv)
      || ops.reduce((a, op) => a + (parseFloat(op.smv) || 0), 0);

    return {
      id: s.id, styleRef,
      brand:   s.brand    || s.brandName    || '',
      product: s.product  || s.productType  || s.garmentType || s.category || '',
      fabric:  s.fabric   || s.fabricType   || '',
      buyer:   s.buyer    || s.buyerName    || '',
      season:  s.season   || '',
      colors, sizes, pos, totalQty, sizeColorMatrix,
      ops, totalSMV,
      opCount: ops.length,
      source:  'NIRVIKALP',
      _raw: s,
    };
  },

  /** Size-qty breakdown for a specific PO (mirrors getPOSizeColor) */
  getPOSizeQty: (styleRef, poNo, db) => {
    const rows = (db?.poSizeTable || []).filter(r =>
      r.style_ref === styleRef && (!poNo || r.po_no === poNo)
    );
    return rows.map(r => ({ color: r.color, size: r.size, qty: parseInt(r.qty) || 0 }));
  },
};

// ─── EFFICIENCY CALCULATIONS (mirrors ACCU source code) ──────────────────────
const eff = {
  // Section Eff% = (Output × SMV) / (AvailMin × Workers) × 100
  section: (output, smv, availMin, workers) =>
    (!availMin || !workers || !smv) ? 0
    : Math.round((output * smv) / (availMin * workers) * 100),
  // Operator Eff% = (Done × OpSMV) / PeriodMin × 100
  operator: (done, opSmv, periodMin) =>
    (!periodMin || !opSmv) ? 0
    : Math.round((done * opSmv) / periodMin * 100),
  // Net available minutes from shift config
  netMin: (shiftHrs, lunchMin = 30, teaMin = 10) =>
    Math.max(0, shiftHrs * 60 - lunchMin - teaMin),
  // Number of scan periods
  periods: (workHrs, durHrs, breakMin = 40) =>
    Math.max(0, Math.floor((workHrs * 60 - breakMin) / (durHrs * 60))),
};

// ─── BARCODE GENERATION ───────────────────────────────────────────────────────
const BC = {
  line:    (factId, lineId) => `LINE${String(factId).slice(-3)}${String(lineId).padStart(4,'0')}`,
  worker:  (opId) => `OP${String(opId).replace(/\D/g,'').padStart(6,'0')}`,
  period:  (date, lineId, no) => `PER${date.replace(/-/g,'')}L${String(lineId).padStart(3,'0')}P${String(no).padStart(2,'0')}`,
  bundle:  (planId, layNo, bndNo) => `BND${String(planId).slice(-6)}L${String(layNo).padStart(2,'0')}B${String(bndNo).padStart(3,'0')}`,
  opBundle:(planId, layNo, bndNo, opIdx) => `OPB${String(planId).slice(-5)}L${layNo}B${String(bndNo).padStart(3,'0')}O${String(opIdx).padStart(2,'0')}`,
  product: (planId, layNo, bndNo, gNo) => `PRD${String(planId).slice(-5)}L${layNo}B${String(bndNo).padStart(3,'0')}G${String(gNo).padStart(3,'0')}`,
};

// ─── API SERVICE ─────────────────────────────────────────────────────────────
const pmApi = {
  _available: null,
  async checkAvailable() {
    if (this._available !== null) return this._available;
    try {
      const r = await fetch(PM_API + '?action=load_all&_check=1', { signal: AbortSignal.timeout(3000) });
      this._available = r.ok;
    } catch { this._available = false; }
    return this._available;
  },
  async loadAll() {
    try {
      const r = await fetch(PM_API + '?action=load_all');
      const j = await r.json();
      return j.success ? j.data : null;
    } catch { return null; }
  },
  async save(table, data) {
    try {
      const r = await fetch(PM_API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save', table, data })
      });
      return await r.json();
    } catch { return { success: false }; }
  },
  async batchSave(table, rows) {
    try {
      const r = await fetch(PM_API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'batch_save', table, rows })
      });
      return await r.json();
    } catch { return { success: false }; }
  },
  async mis(report, from, to, lineId) {
    try {
      const url = `${PM_API}?action=mis&report=${report}&from=${from}&to=${to}${lineId?`&line_id=${lineId}`:''}`;
      const r = await fetch(url);
      const j = await r.json();
      return j.rows || null;
    } catch { return null; }
  },
};

// ─── LOCAL STORAGE ────────────────────────────────────────────────────────────
const LS = {
  get: (k)    => { try { return JSON.parse(localStorage.getItem(`pm_${k}`)) || null; } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(`pm_${k}`, JSON.stringify(v)); } catch {} },
};

// ─── THEME ────────────────────────────────────────────────────────────────────
// Data source colours:
//  NIRVIKALP = blue badge (fetched, read-only)
//  LOCAL/NEW  = amber border (new entry, highlighted)
//  IMPORTED   = green badge
const SRC = {
  badge: (src) => ({
    NIRVIKALP: { bg:'#eff6ff', color:'#0D47A1', border:'#90CAF9', text:'From Nirvikalp' },
    LOCAL:      { bg:'#FFF8E1', color:'#b45309', border:'#fcd34d', text:'New Entry' },
    IMPORTED:   { bg:'#f0fdf4', color:'#15803d', border:'#86efac', text:'Imported' },
  }[src] || { bg:'#F8F9FA', color:'#6C757D', border:'#DEE2E6', text:src }),
  fieldBorder: (isNew) => isNew ? '2px solid #FFB300' : '1px solid #CED4DA',
  fieldBg:     (isNew) => isNew ? '#FFF8E1' : '#F8F9FA',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg:#f0f2f5; --surf:#fff; --panel:#F8F9FA; --bdr:#DEE2E6; --bdr2:#CED4DA;
  --acc:#1565C0; --acc-bg:#eff6ff; --acc2:#6A1B9A; --acc2-bg:#EDE7F6;
  --grn:#388E3C; --grn-bg:#C8E6C9; --warn:#E65100; --warn-bg:#FFF8E1;
  --red:#C62828;  --red-bg:#FFCDD2; --amber:#b45309; --amber-bg:#FFF8E1;
  --tx:#0D1B2A; --tx2:#2C3E50; --mu:#6C757D; --faint:#ADB5BD;
  --fn:'Inter',sans-serif; --mo:'JetBrains Mono',monospace;
  --base:15px; --sm:13px; --xs:12px; --xxs:11px; --lg:17px; --xl:22px;
}
.pm { font-family:var(--fn); font-size:var(--base); background:var(--bg); color:var(--tx); height:100%; display:flex; flex-direction:column; overflow:hidden; -webkit-font-smoothing:antialiased }

/* Shell */
.pm-top { height:50px; background:var(--surf); border-bottom:1px solid var(--bdr); display:flex; align-items:center; padding:0 20px; gap:14px; flex-shrink:0; box-shadow:0 1px 4px rgba(0,0,0,.06) }
.pm-logo { font-family:var(--mo); font-size:16px; font-weight:700; color:var(--acc); letter-spacing:2px }
.pm-sep { width:1px; height:16px; background:var(--bdr2) }
.pm-sub { font-size:var(--sm); color:var(--mu) }
.pm-sp { flex:1 }

/* Connection badge */
.conn-badge { font-size:var(--xxs); font-weight:600; padding:3px 10px; border-radius:20px; border:1px solid; display:flex; align-items:center; gap:5px }
.conn-badge.live    { color:#15803d; background:#f0fdf4; border-color:#86efac }
.conn-badge.local   { color:#b45309; background:#FFF8E1; border-color:#fcd34d }
.conn-badge.checking{ color:#6C757D; background:#F8F9FA; border-color:#CED4DA }

/* Tabs */
.pm-tabs { display:flex; background:var(--surf); border-bottom:2px solid var(--bdr); flex-shrink:0 }
.pm-tab { padding:11px 20px; font-family:var(--fn); font-size:var(--sm); font-weight:600; color:var(--mu); border:none; background:none; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; transition:all .15s }
.pm-tab:hover { color:var(--acc); background:var(--acc-bg) }
.pm-tab.on { color:var(--acc); border-bottom-color:var(--acc) }

/* Source badges */
.src-badge { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:700; padding:2px 7px; border-radius:20px; border:1px solid; white-space:nowrap }

/* Layout */
.layout-master { display:flex; flex:1; overflow:hidden }
.side-rail { width:240px; min-width:240px; background:var(--surf); border-right:1px solid var(--bdr); display:flex; flex-direction:column; overflow-y:auto }
.side-hdr { padding:10px 16px; background:var(--panel); border-bottom:1px solid var(--bdr); font-size:var(--xxs); font-weight:700; color:var(--mu); letter-spacing:1px; text-transform:uppercase }
.side-btn { display:flex; align-items:center; gap:8px; width:100%; padding:10px 16px; border:none; border-left:3px solid transparent; background:none; color:var(--tx2); font-size:var(--sm); cursor:pointer; text-align:left; transition:all .12s; border-bottom:1px solid var(--bdr) }
.side-btn:hover { background:var(--panel); color:var(--tx) }
.side-btn.on { background:var(--acc-bg); border-left-color:var(--acc); color:var(--acc); font-weight:600 }
.content-area { flex:1; overflow-y:auto; padding:20px; background:var(--bg) }

/* Cards */
.card { background:var(--surf); border:1px solid var(--bdr); border-radius:8px; padding:20px; margin-bottom:16px; box-shadow:0 1px 3px rgba(0,0,0,.04) }
.card h3 { font-size:var(--sm); font-weight:700; color:var(--tx); margin-bottom:14px; padding-bottom:10px; border-bottom:1px solid var(--bdr); display:flex; align-items:center; gap:8px; flex-wrap:wrap }

/* Fetched data section */
.fetched-block { background:var(--acc-bg); border:1px solid #90CAF9; border-radius:8px; padding:14px 16px; margin-bottom:14px }
.fetched-block .block-hdr { font-size:var(--xs); font-weight:700; color:var(--acc); margin-bottom:10px; display:flex; align-items:center; gap:6px }

/* New data section (amber) */
.new-block { background:var(--amber-bg); border:2px solid #FFB300; border-radius:8px; padding:14px 16px; margin-bottom:14px }
.new-block .block-hdr { font-size:var(--xs); font-weight:700; color:var(--amber); margin-bottom:10px; display:flex; align-items:center; gap:6px }

/* Forms */
.form-row { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; margin-bottom:14px }
.fg { display:flex; flex-direction:column; gap:4px }
.fl { font-size:var(--xs); font-weight:500; color:var(--mu) }
.fl.new { color:var(--amber); font-weight:700 }
.inp, .sel { background:var(--panel); border:1px solid var(--bdr2); color:var(--tx); font-family:var(--fn); font-size:var(--sm); padding:7px 10px; border-radius:6px; outline:none; transition:border-color .15s }
.inp:focus, .sel:focus { border-color:var(--acc); box-shadow:0 0 0 3px rgba(37,99,235,.1) }
.inp.new-field { border:2px solid #FFB300; background:#FFF8E1 }
.inp.new-field:focus { border-color:var(--amber); box-shadow:0 0 0 3px rgba(217,119,6,.15) }
.inp.readonly { background:#F8F9FA; color:var(--tx2); cursor:default; border-style:dashed }

/* Buttons */
.btn { padding:7px 14px; border:none; border-radius:6px; font-family:var(--fn); font-size:var(--sm); font-weight:600; cursor:pointer; transition:all .15s; display:inline-flex; align-items:center; gap:6px }
.btn-p { background:var(--acc); color:#fff; box-shadow:0 1px 3px rgba(37,99,235,.3) }
.btn-p:hover { background:#0D47A1 }
.btn-s { background:var(--panel); color:var(--tx2); border:1px solid var(--bdr2) }
.btn-s:hover { background:var(--bdr) }
.btn-g { background:var(--grn); color:#fff }
.btn-g:hover { background:#15803d }
.btn-2 { background:var(--acc2); color:#fff }
.btn:disabled { opacity:.4; cursor:default }

/* Tables */
.tbl { width:100%; border-collapse:separate; border-spacing:0; font-size:var(--sm); border:1px solid var(--bdr); border-radius:8px; overflow:hidden }
.tbl th { background:var(--panel); padding:9px 12px; text-align:left; font-size:var(--xs); font-weight:700; color:var(--mu); letter-spacing:.4px; text-transform:uppercase; border-bottom:2px solid var(--bdr2) }
.tbl td { padding:8px 12px; border-bottom:1px solid var(--bdr); color:var(--tx) }
.tbl tr:last-child td { border-bottom:none }
.tbl tr:hover td { background:#f5f8ff }
.btn-edit { padding:3px 8px; font-size:var(--xs); background:var(--acc-bg); color:var(--acc); border:1px solid #90CAF9; border-radius:4px; cursor:pointer; margin-right:4px }
.btn-del  { padding:3px 8px; font-size:var(--xs); background:var(--red-bg);  color:var(--red);  border:1px solid #fecaca; border-radius:4px; cursor:pointer }

/* Stats strip */
.stats { display:flex; background:var(--surf); border:1px solid var(--bdr); border-radius:8px; overflow:hidden; margin-bottom:16px }
.stat-b { flex:1; padding:12px 16px; border-right:1px solid var(--bdr) }
.stat-b:last-child { border-right:none }
.stat-l { font-size:var(--xxs); color:var(--mu); font-weight:600; text-transform:uppercase; letter-spacing:.4px; margin-bottom:3px }
.stat-v { font-family:var(--mo); font-size:var(--xl); font-weight:700; line-height:1.1 }
.stat-s { font-size:var(--xs); color:var(--mu); margin-top:2px }

/* Pills */
.pill { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:var(--xxs); font-weight:700 }
.pill-g  { background:var(--grn-bg);   color:var(--grn)  }
.pill-w  { background:var(--warn-bg);  color:var(--warn) }
.pill-r  { background:var(--red-bg);   color:var(--red)  }
.pill-a  { background:var(--acc-bg);   color:var(--acc)  }
.pill-2  { background:var(--acc2-bg);  color:var(--acc2) }
.pill-am { background:var(--amber-bg); color:var(--amber)}

/* Barcode grid */
.bc-grid { display:flex; flex-wrap:wrap; gap:12px; padding:8px 0 }

/* Scan sequence */
.scan-seq { display:flex; align-items:center; gap:0; flex-wrap:wrap; padding:10px 0 }
.scan-nd { padding:6px 14px; border-radius:20px; font-size:var(--sm); font-weight:700; white-space:nowrap; border:2px solid transparent }
.scan-nd.active { border-color:var(--acc) }
.scan-nd.done { opacity:.7 }
.s-per  { background:#BBDEFB; color:#1e40af }
.s-line { background:#d1fae5; color:#065f46 }
.s-op   { background:#E1BEE7; color:#5b21b6 }
.s-bnd  { background:#FFE082; color:#92400e }
.scan-arrow { color:var(--faint); font-size:13px; margin:0 8px }

/* MIS */
.mis-list { padding:6px }
.mis-list button { display:flex; align-items:center; gap:8px; width:100%; padding:8px 10px; background:none; border:1px solid transparent; border-radius:6px; font-size:var(--sm); color:var(--tx2); cursor:pointer; text-align:left; margin-bottom:2px; transition:all .12s }
.mis-list button:hover { background:var(--panel) }
.mis-list button.on { background:var(--acc-bg); border-color:#90CAF9; color:var(--acc); font-weight:600 }
.mis-code { font-family:var(--mo); font-size:var(--xxs); background:var(--panel); border:1px solid var(--bdr2); padding:1px 6px; border-radius:4px; color:var(--mu); min-width:44px; text-align:center }
button.on .mis-code { background:var(--acc); color:#fff; border-color:var(--acc) }

/* Alert */
.alert { padding:10px 14px; border-radius:6px; font-size:var(--sm); margin-bottom:12px; display:flex; align-items:center; gap:8px }
.alert.ok  { background:var(--grn-bg);  color:var(--grn);  border:1px solid #86efac }
.alert.err { background:var(--red-bg);  color:var(--red);  border:1px solid #fca5a5 }
.alert.inf { background:var(--acc-bg);  color:var(--acc);  border:1px solid #90CAF9 }
.alert.warn{ background:var(--warn-bg); color:var(--warn); border:1px solid #fcd34d }

/* Spinner */
.spin { width:22px; height:22px; border:3px solid var(--bdr2); border-top-color:var(--acc); border-radius:50%; animation:rsp .6s linear infinite; display:inline-block; flex-shrink:0 }
@keyframes rsp { to { transform:rotate(360deg) } }
.empty-st { text-align:center; padding:48px; color:var(--mu) }
.empty-st .ei { font-size:40px; opacity:.2; margin-bottom:8px }
.empty-st .et { font-size:var(--sm) }

/* Legend */
.legend { display:flex; gap:10px; align-items:center; font-size:var(--xs); color:var(--mu); margin-bottom:12px; flex-wrap:wrap }
.legend-item { display:flex; align-items:center; gap:5px }

/* Print */
@media print {
  .pm-top, .pm-tabs, .side-rail, .no-print { display:none !important }
  .bc-grid { display:flex !important; flex-wrap:wrap }
  .bc-grid > * { page-break-inside:avoid }
}
`;

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
function Alert({ msg, type = 'inf', onClose }) {
  if (!msg) return null;
  return (
    <div className={`alert ${type}`}>
      {msg}
      {onClose && <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✕</button>}
    </div>
  );
}

function SrcBadge({ src }) {
  const s = SRC.badge(src);
  return <span className="src-badge" style={{ background: s.bg, color: s.color, borderColor: s.border }}>{s.text}</span>;
}

function Stats({ items }) {
  return (
    <div className="stats">
      {items.map(s => (
        <div key={s.label} className="stat-b">
          <div className="stat-l">{s.label}</div>
          <div className="stat-v" style={s.color ? { color: s.color } : {}}>{s.val ?? '—'}</div>
          {s.sub && <div className="stat-s">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function Legend() {
  return (
    <div className="legend no-print">
      <strong>Data source:</strong>
      <span className="legend-item"><span className="src-badge" style={{ background:'#eff6ff', color:'#0D47A1', borderColor:'#90CAF9', border:'1px solid' }}>From Nirvikalp</span> Auto-fetched, read-only</span>
      <span className="legend-item"><span className="src-badge" style={{ background:'#FFF8E1', color:'#b45309', borderColor:'#FFB300', border:'1px solid' }}>New Entry</span> Enter once here</span>
      <span className="legend-item"><span className="src-badge" style={{ background:'#f0fdf4', color:'#15803d', borderColor:'#86efac', border:'1px solid' }}>Imported</span> From scan file</span>
    </div>
  );
}

function ReadField({ label, value, wide }) {
  return (
    <div className="fg">
      <span className="fl">{label} <SrcBadge src="NIRVIKALP" /></span>
      <input className="inp readonly" style={{ width: wide || 160 }} value={value || '—'} readOnly />
    </div>
  );
}

// ─── 1. FACTORY PROFILE ───────────────────────────────────────────────────────
function FactoryProfile({ db, pmDb, setPmDb, apiLive }) {
  const [sub, setSub]   = useState('lines');
  const [alert, setA]   = useState(null);
  const [saving, setSv] = useState(false);

  const [atForm, setAt] = useState({
    date: new Date().toISOString().slice(0, 10),
    shiftHours: 10, lunchMin: 30, teaMin: 10,
    otHours: 0, periodDuration: 2, lineId: '',
  });

  const [wkIn, setWk] = useState({ name: '', operatorId: '', designation: 'Operator' });

  const factories = bridge.getFactories(db);
  const allLines  = bridge.getAllLines(db);

  const netMin   = eff.netMin(atForm.shiftHours, atForm.lunchMin, atForm.teaMin);
  const nPeriods = eff.periods(atForm.shiftHours, atForm.periodDuration, atForm.lunchMin + atForm.teaMin);

  const lineConfig = (lineId, factId) =>
    (pmDb.lineConfigs || []).find(lc => lc.line_id == lineId && lc.factory_id == factId);

  const genLineBC = async (line) => {
    const bc = BC.line(line.factoryId, line.id);
    const cfg = {
      line_id: line.id, factory_id: line.factoryId,
      factory_name: line.factoryName, line_name: line.name || line.category || `Line ${line.id}`,
      barcode: bc, machines: line.machines || 0, eff: line.eff || 0,
      source_flag: 'LOCAL',
    };
    const updated = {
      ...pmDb,
      lineConfigs: [...(pmDb.lineConfigs || []).filter(l => !(l.line_id == line.id && l.factory_id == line.factoryId)), cfg]
    };
    setPmDb(updated);
    LS.set('lineConfigs', updated.lineConfigs);
    if (apiLive) await pmApi.save('pm_line_config', cfg);
    setA({ msg: `Barcode generated: ${bc}`, type: 'ok' });
  };

  const saveWorker = async (lineId, factId) => {
    if (!wkIn.name || !wkIn.operatorId) return setA({ msg: 'Name + Operator ID required', type: 'err' });
    setSv(true);
    const w = {
      id: Date.now(), line_id: lineId, factory_id: factId,
      operator_id: wkIn.operatorId, name: wkIn.name, designation: wkIn.designation,
      worker_barcode: BC.worker(wkIn.operatorId), source_flag: 'LOCAL',
      created_at: new Date().toISOString(),
    };
    const updated = { ...pmDb, workers: [...(pmDb.workers || []), w] };
    setPmDb(updated);
    LS.set('workers', updated.workers);
    if (apiLive) await pmApi.save('pm_worker', w);
    setWk({ name: '', operatorId: '', designation: 'Operator' });
    setSv(false);
    setA({ msg: `Worker ${w.name} saved — BC: ${w.worker_barcode}`, type: 'ok' });
  };

  const uploadCSV = (e, lineId, factId) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const lines = ev.target.result.split('\n').slice(1).filter(l => l.trim());
      const workers = lines.map((l, i) => {
        const [name, operatorId, designation] = l.split(',').map(x => x.trim());
        return { id: Date.now() + i, line_id: lineId, factory_id: factId, name,
          operator_id: operatorId || `AUTO${Date.now()+i}`, designation: designation || 'Operator',
          worker_barcode: BC.worker(operatorId || (Date.now()+i)), source_flag: 'IMPORTED' };
      });
      const updated = { ...pmDb, workers: [...(pmDb.workers || []), ...workers] };
      setPmDb(updated);
      LS.set('workers', updated.workers);
      if (apiLive) await pmApi.batchSave('pm_worker', workers);
      setA({ msg: `${workers.length} workers imported`, type: 'ok' });
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const saveAvailTime = async () => {
    setSv(true);
    const entry = { id: Date.now(), ...atForm,
      net_minutes: netMin, periods_count: nPeriods, source_flag: 'LOCAL',
      created_at: new Date().toISOString() };
    const updated = { ...pmDb, availableTimes: [...(pmDb.availableTimes || []).filter(a => !(a.date === atForm.date && a.lineId === atForm.lineId)), entry] };
    setPmDb(updated);
    LS.set('availableTimes', updated.availableTimes);
    if (apiLive) await pmApi.save('pm_available_time', entry);
    setSv(false);
    setA({ msg: `Available time saved for ${atForm.date}`, type: 'ok' });
  };

  return (
    <div className="layout-master">
      <div className="side-rail">
        <div className="side-hdr">Factory Profile</div>
        {[['lines','🏭 Production Lines'],['availTime','⏱ Available Time'],['workers','👥 Workers']].map(([k,l])=>(
          <button key={k} className={`side-btn ${sub===k?'on':''}`} onClick={()=>setSub(k)}>{l}</button>
        ))}
        <div style={{ padding:10, fontSize:12, color:'#6C757D', lineHeight:1.6, borderTop:'1px solid #DEE2E6', marginTop:'auto' }}>
          <strong>From Sourcing:</strong> {factories.length} factories, {allLines.length} lines auto-loaded
        </div>
      </div>
      <div className="content-area">
        {alert && <Alert msg={alert.msg} type={alert.type} onClose={()=>setA(null)} />}
        <Legend />

        {sub === 'lines' && (
          <>
            <Stats items={[
              { label:'Factories (Sourcing)', val:factories.length, color:'#1565C0' },
              { label:'Total Lines (Sourcing)', val:allLines.length },
              { label:'BCs Generated', val:(pmDb.lineConfigs||[]).length, color:'#388E3C' },
              { label:'Workers Saved', val:(pmDb.workers||[]).length, color:'#6A1B9A' },
            ]} />

            {factories.length === 0 ? (
              <div className="empty-st"><div className="ei">🏭</div><div className="et">No Garment Supplier factories in Sourcing module yet</div></div>
            ) : factories.map(fac => (
              <div className="card" key={fac.id}>
                <h3>🏭 {fac.name} <SrcBadge src="NIRVIKALP" /></h3>

                {/* Fetched data — read-only */}
                <div className="fetched-block">
                  <div className="block-hdr">📊 Lines from Sourcing (auto-fetched, read-only)</div>
                  {bridge.getLines(fac).length === 0 ? (
                    <div style={{ color:'#6C757D', fontSize:13 }}>No lines defined in Sourcing for this factory</div>
                  ) : (
                    <table className="tbl">
                      <thead><tr><th>Line</th><th>Category</th><th>Machines</th><th>Eff%</th><th>Helpers</th><th>QC</th><th>Barcode</th><th>Action</th></tr></thead>
                      <tbody>
                        {bridge.getLines(fac).map(line => {
                          const cfg = lineConfig(line.id, fac.id);
                          return (
                            <tr key={line.id}>
                              <td><strong>{line.name || line.category || `Line ${line.id}`}</strong></td>
                              <td>{line.category || '—'}</td>
                              <td>{line.machines || '—'}</td>
                              <td>{line.eff ? `${line.eff}%` : '—'}</td>
                              <td>{line.helpers || '—'}</td>
                              <td>{line.qc || '—'}</td>
                              <td>
                                {cfg
                                  ? <><Barcode value={cfg.barcode} height={24} fontSize={8} width={1.2} displayValue={false}/><span style={{fontFamily:'monospace',fontSize:10}}>{cfg.barcode}</span></>
                                  : <span className="pill pill-w">No BC yet</span>}
                              </td>
                              <td>
                                {/* NEW ACTION — highlighted */}
                                <button className="btn btn-g" style={{ fontSize:11, padding:'3px 8px' }}
                                  onClick={() => genLineBC({...line, factoryId:fac.id, factoryName:fac.name})}>
                                  {cfg ? '🔄 Regen' : '➕ Gen BC'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {sub === 'availTime' && (
          <div className="card">
            <h3>⏱ Available Time Setup</h3>
            {/* All new data */}
            <div className="new-block">
              <div className="block-hdr">⚠️ New Entry Required (not in Nirvikalp)</div>
              <div className="form-row">
                <div className="fg"><span className="fl new">Date ★</span><input type="date" className="inp new-field" style={{width:160}} value={atForm.date} onChange={e=>setAt({...atForm,date:e.target.value})}/></div>
                <div className="fg"><span className="fl new">Line ★</span>
                  <select className="sel new-field" value={atForm.lineId} onChange={e=>setAt({...atForm,lineId:e.target.value})}>
                    <option value="">— Select Line —</option>
                    {allLines.map(l=><option key={`${l.factoryId}-${l.id}`} value={l.id}>{l.factoryName} · {l.name||l.category||`Line ${l.id}`}</option>)}
                  </select>
                </div>
                <div className="fg"><span className="fl new">Shift Hours ★</span><input type="number" className="inp new-field" style={{width:120}} value={atForm.shiftHours} onChange={e=>setAt({...atForm,shiftHours:+e.target.value})}/></div>
                <div className="fg"><span className="fl new">Lunch (min)</span><input type="number" className="inp new-field" style={{width:100}} value={atForm.lunchMin} onChange={e=>setAt({...atForm,lunchMin:+e.target.value})}/></div>
                <div className="fg"><span className="fl new">Tea (min)</span><input type="number" className="inp new-field" style={{width:100}} value={atForm.teaMin} onChange={e=>setAt({...atForm,teaMin:+e.target.value})}/></div>
                <div className="fg"><span className="fl new">Period Duration (hr) ★</span><input type="number" step="0.5" className="inp new-field" style={{width:130}} value={atForm.periodDuration} onChange={e=>setAt({...atForm,periodDuration:+e.target.value})}/></div>
              </div>
              <Stats items={[
                { label:'Net Minutes', val:netMin, color:'#1565C0' },
                { label:'Scan Periods', val:nPeriods, color:'#6A1B9A', sub:`${atForm.periodDuration}hr each` },
                { label:'Min / Period', val:Math.floor(atForm.periodDuration*60) },
              ]} />
              <button className="btn btn-p" onClick={saveAvailTime} disabled={saving}>{saving?<span className="spin"/>:null} 💾 Save</button>
            </div>

            {(pmDb.availableTimes||[]).length > 0 && (
              <table className="tbl" style={{marginTop:14}}>
                <thead><tr><th>Date</th><th>Line</th><th>Shift</th><th>Net Min</th><th>Periods</th><th>OT</th><th>Source</th></tr></thead>
                <tbody>
                  {(pmDb.availableTimes||[]).slice().reverse().map(a => (
                    <tr key={a.id}><td>{a.date}</td><td>{a.lineId||'All'}</td><td>{a.shiftHours}h</td><td>{a.net_minutes||a.netMinutes}m</td><td>{a.periods_count||a.periods}</td><td>{a.otHours||0}h</td><td><SrcBadge src={a.source_flag||'LOCAL'}/></td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {sub === 'workers' && allLines.map(line => {
          const cfg = lineConfig(line.id, line.factoryId);
          const workers = (pmDb.workers||[]).filter(w => w.line_id==line.id && w.factory_id==line.factoryId);
          return (
            <div className="card" key={`${line.factoryId}-${line.id}`}>
              <h3>👥 {line.factoryName} — {line.name||line.category||`Line ${line.id}`}
                {cfg && <span className="pill pill-a" style={{fontFamily:'monospace',fontSize:11,marginLeft:'auto'}}>{cfg.barcode}</span>}
              </h3>

              {/* Fetched machines/eff */}
              <div className="fetched-block" style={{marginBottom:10}}>
                <div className="block-hdr">From Sourcing</div>
                <div style={{display:'flex',gap:20,fontSize:13}}>
                  <span>Machines: <strong>{line.machines||'?'}</strong></span>
                  <span>Efficiency: <strong>{line.eff||'?'}%</strong></span>
                  <span>Helpers: <strong>{line.helpers||0}</strong></span>
                  <span>Workers in PM: <strong>{workers.length}</strong></span>
                </div>
              </div>

              {/* New worker entry */}
              <div className="new-block">
                <div className="block-hdr">⚠️ Add Workers (enter once, used for barcodes)</div>
                <div className="form-row">
                  <div className="fg"><span className="fl new">Operator ID ★</span><input className="inp new-field" style={{width:120}} placeholder="OP001" value={wkIn.operatorId} onChange={e=>setWk({...wkIn,operatorId:e.target.value})}/></div>
                  <div className="fg"><span className="fl new">Name ★</span><input className="inp new-field" style={{width:180}} placeholder="Full name" value={wkIn.name} onChange={e=>setWk({...wkIn,name:e.target.value})}/></div>
                  <div className="fg"><span className="fl new">Designation</span>
                    <select className="sel new-field" value={wkIn.designation} onChange={e=>setWk({...wkIn,designation:e.target.value})}>
                      {['Operator','Helper','Ironer','QC','Packer','Thread Cutter'].map(d=><option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <button className="btn btn-p" style={{alignSelf:'flex-end'}} onClick={()=>saveWorker(line.id,line.factoryId)} disabled={saving}>
                    {saving?<span className="spin"/>:null} Add Worker
                  </button>
                  <label className="btn btn-s" style={{alignSelf:'flex-end',cursor:'pointer'}}>
                    📤 Upload CSV <input type="file" accept=".csv" style={{display:'none'}} onChange={e=>uploadCSV(e,line.id,line.factoryId)}/>
                  </label>
                </div>
                <div style={{fontSize:11,color:'#6C757D'}}>CSV: Name, OperatorID, Designation (no header row)</div>
              </div>

              {workers.length > 0 && (
                <table className="tbl">
                  <thead><tr><th>ID</th><th>Name</th><th>Designation</th><th>Barcode</th><th>Source</th></tr></thead>
                  <tbody>
                    {workers.map(w=>(
                      <tr key={w.id}>
                        <td style={{fontFamily:'monospace'}}>{w.operator_id}</td>
                        <td>{w.name}</td>
                        <td>{w.designation}</td>
                        <td><Barcode value={w.worker_barcode} height={28} fontSize={8} width={1.3} /></td>
                        <td><SrcBadge src={w.source_flag}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── 2. CUT PLAN ─────────────────────────────────────────────────────────────
function CutPlan({ db, pmDb, setPmDb, apiLive }) {
  const [sub, setSub]     = useState('plan');
  const [selStyleId, setSS] = useState('');
  const [selPO, setPO]    = useState('');
  const [alert, setA]     = useState(null);
  const [saving, setSv]   = useState(false);

  const [layForm, setLF] = useState({ layNo:1, plyFrom:1, plyTo:1, cutQty:0, bundleQty:12, size:'', color:'' });

  const styles    = bridge.getStyles ? (db?.mgmtStyles||[]) : (db?.mgmtStyles||[]);
  const styleData = selStyleId ? bridge.getStyleData(selStyleId, db) : null;
  const plans     = (pmDb.cutPlans||[]).filter(p=>p.style_id===selStyleId);

  // Size × color matrix for selected PO
  const sizeColorRows = styleData
    ? bridge.getPOSizeQty(styleData.styleRef, selPO, db)
    : [];

  const createPlan = async () => {
    if (!selStyleId || !styleData) return setA({ msg:'Select a style', type:'err' });
    setSv(true);
    const plan = {
      id: Date.now(), style_id: selStyleId, style_ref: styleData.styleRef,
      style_name: styleData.styleRef, order_no: selPO,
      total_smv: styleData.totalSMV, ops_count: styleData.opCount,
      source_flag: 'LOCAL', created_at: new Date().toISOString(),
    };
    const upd = { ...pmDb, cutPlans:[...(pmDb.cutPlans||[]),plan] };
    setPmDb(upd); LS.set('cutPlans',upd.cutPlans);
    if (apiLive) await pmApi.save('pm_cut_plan', plan);
    setSv(false);
    setA({ msg:`Cut plan created for ${styleData.styleRef}`, type:'ok' });
  };

  const addLay = async (planId) => {
    if (!layForm.size) return setA({ msg:'Select size', type:'err' });
    setSv(true);
    const ops = styleData?.ops || [];
    const bc  = Math.ceil(layForm.cutQty / layForm.bundleQty) || 1;
    const bundles=[], barcodes=[];

    for (let b=1; b<=bc; b++) {
      const bQty = b===bc ? ((layForm.cutQty % layForm.bundleQty)||layForm.bundleQty) : layForm.bundleQty;
      const bndId = `${planId}-L${layForm.layNo}-B${b}`;
      const bndBC = BC.bundle(planId, layForm.layNo, b);
      bundles.push({ id:bndId, plan_id:planId, lay_no:layForm.layNo, bundle_no:b,
        size:layForm.size, color:layForm.color, qty:bQty, barcode:bndBC, source_flag:'LOCAL' });

      // Operation barcodes (1 per operation per bundle)
      ops.forEach((op,oi) => barcodes.push({
        bundle_id:bndId, bundle_no:b, lay_no:layForm.layNo,
        op_code:op.procName||`OP${oi+1}`, op_name:op.procName, part_name:op.partName,
        machine:op.machine, smv:op.smv,
        barcode:BC.opBundle(planId,layForm.layNo,b,oi),
        barcode_type:'OPERATION', plan_id:planId,
      }));
      // Product QR (1 per garment)
      Array.from({length:bQty},(_,gi) => barcodes.push({
        bundle_id:bndId, bundle_no:b, lay_no:layForm.layNo, garment_no:gi+1,
        qr:BC.product(planId,layForm.layNo,b,gi+1),
        barcode_type:'PRODUCT', plan_id:planId,
      }));
    }

    const lay = { id:`${planId}-L${layForm.layNo}`, plan_id:planId, ...layForm, bundle_count:bc, source_flag:'LOCAL' };
    const upd = { ...pmDb, lays:[...(pmDb.lays||[]),lay], bundles:[...(pmDb.bundles||[]),...bundles], barcodes:[...(pmDb.barcodes||[]),...barcodes] };
    setPmDb(upd);
    LS.set('lays',upd.lays); LS.set('bundles',upd.bundles); LS.set('barcodes',upd.barcodes);
    if (apiLive) { await pmApi.save('pm_lay',lay); await pmApi.batchSave('pm_bundle',bundles); }
    setLF(f=>({...f,layNo:f.layNo+1}));
    setSv(false);
    setA({ msg:`Lay ${lay.layNo}: ${bc} bundles, ${barcodes.length} barcodes generated`, type:'ok' });
  };

  return (
    <div className="layout-master">
      <div className="side-rail">
        <div className="side-hdr">Cut Plan</div>
        {[['plan','📋 Plans'],['lays','📐 Lays'],['barcodes','🔷 Barcodes']].map(([k,l])=>(
          <button key={k} className={`side-btn ${sub===k?'on':''}`} onClick={()=>setSub(k)}>{l}</button>
        ))}
        <div style={{padding:12, borderTop:'1px solid #DEE2E6'}}>
          <div className="fg" style={{marginBottom:8}}>
            <span className="fl">Style (from Registry)</span>
            <select className="sel" style={{width:'100%'}} value={selStyleId} onChange={e=>{ setSS(e.target.value); setPO(''); }}>
              <option value="">— Select Style —</option>
              {(db?.mgmtStyles||[]).map(s=><option key={s.id} value={s.id}>{s.styleRef||s.name||s.id}</option>)}
            </select>
          </div>
          {styleData?.pos?.length > 0 && (
            <div className="fg">
              <span className="fl">Order / PO</span>
              <select className="sel" style={{width:'100%'}} value={selPO} onChange={e=>setPO(e.target.value)}>
                <option value="">— All POs —</option>
                {styleData.pos.map(po=><option key={po.num} value={po.num}>{po.num} ({po.qty} pcs)</option>)}
              </select>
            </div>
          )}
          {styleData && (
            <div style={{marginTop:10,background:'#eff6ff',border:'1px solid #90CAF9',borderRadius:6,padding:10,fontSize:12}}>
              <div><SrcBadge src="NIRVIKALP" /> Auto-fetched:</div>
              <div>SMV: <strong>{styleData.totalSMV.toFixed(3)}</strong> min</div>
              <div>Operations: <strong>{styleData.opCount}</strong></div>
              <div>Colors: <strong>{styleData.colors.join(', ')||'—'}</strong></div>
              <div>Sizes: <strong>{styleData.sizes.join(', ')||'—'}</strong></div>
              <div>Total Qty: <strong>{styleData.totalQty.toLocaleString()}</strong></div>
            </div>
          )}
        </div>
      </div>

      <div className="content-area">
        {alert && <Alert msg={alert.msg} type={alert.type} onClose={()=>setA(null)} />}
        <Legend />

        {sub === 'plan' && !selStyleId && (
          <div className="empty-st"><div className="ei">📋</div><div className="et">Select a style from the sidebar</div></div>
        )}

        {sub === 'plan' && styleData && (
          <>
            {/* Style data — fetched read-only */}
            <div className="card">
              <h3>Style Details <SrcBadge src="NIRVIKALP" /></h3>
              <div className="fetched-block">
                <div className="block-hdr">📊 From Style Registry + Workstation + Sourcing (read-only)</div>
                <div className="form-row">
                  <ReadField label="Style Ref" value={styleData.styleRef} />
                  <ReadField label="Product" value={styleData.product} />
                  <ReadField label="Brand" value={styleData.brand} />
                  <ReadField label="Fabric" value={styleData.fabric} />
                  <ReadField label="Total SMV" value={`${styleData.totalSMV.toFixed(3)} min`} />
                  <ReadField label="Operations" value={styleData.opCount} />
                  <ReadField label="Total Qty" value={styleData.totalQty?.toLocaleString()} />
                </div>

                {/* Colors + Sizes matrix */}
                {sizeColorRows.length > 0 && (
                  <>
                    <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>Size × Color × Qty (from PO upload)</div>
                    <table className="tbl">
                      <thead><tr><th>PO</th><th>Color</th><th>Size</th><th>Qty</th></tr></thead>
                      <tbody>
                        {sizeColorRows.map((r,i)=><tr key={i}><td>{r.poNo||selPO}</td><td>{r.color}</td><td>{r.size}</td><td><strong>{r.qty}</strong></td></tr>)}
                      </tbody>
                    </table>
                  </>
                )}

                {/* Operations list */}
                {styleData.ops.length > 0 && (
                  <>
                    <div style={{fontWeight:600,fontSize:13,margin:'12px 0 8px'}}>Operation Bulletin ({styleData.ops.length} operations)</div>
                    <table className="tbl">
                      <thead><tr><th>#</th><th>Operation</th><th>Part</th><th>Machine</th><th>SMV</th></tr></thead>
                      <tbody>
                        {styleData.ops.map((op,i)=>(
                          <tr key={i}><td>{i+1}</td><td>{op.procName}</td><td>{op.partName}</td><td>{op.machine}</td><td style={{fontFamily:'monospace'}}>{op.smv}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>

              <button className="btn btn-p" onClick={createPlan} disabled={saving}>
                {saving?<span className="spin"/>:null} ➕ Create Cut Plan
              </button>
            </div>

            {plans.map(plan => {
              const planLays    = (pmDb.lays||[]).filter(l=>l.plan_id===plan.id);
              const planBundles = (pmDb.bundles||[]).filter(b=>b.plan_id===plan.id);
              const planBCs     = (pmDb.barcodes||[]).filter(b=>b.plan_id===plan.id);
              return (
                <div className="card" key={plan.id}>
                  <h3>📋 Cut Plan — {plan.style_ref} {plan.order_no&&`· ${plan.order_no}`}
                    <span className="pill pill-am" style={{marginLeft:'auto'}}>
                      {new Date(plan.created_at).toLocaleDateString()}
                    </span>
                  </h3>
                  <Stats items={[
                    { label:'Lays', val:planLays.length, color:'#1565C0' },
                    { label:'Bundles', val:planBundles.length },
                    { label:'Barcodes', val:planBCs.length, color:'#6A1B9A' },
                    { label:'Total SMV', val:`${plan.total_smv?.toFixed(2)||0}m` },
                  ]} />

                  {/* Add Lay — new data, amber */}
                  <div className="new-block">
                    <div className="block-hdr">⚠️ Add Lay (new entry — size/color auto-listed from PO above)</div>
                    <div className="form-row">
                      <div className="fg"><span className="fl new">Lay No ★</span><input type="number" className="inp new-field" style={{width:90}} value={layForm.layNo} onChange={e=>setLF({...layForm,layNo:+e.target.value})}/></div>
                      <div className="fg"><span className="fl new">Ply From</span><input type="number" className="inp new-field" style={{width:90}} value={layForm.plyFrom} onChange={e=>setLF({...layForm,plyFrom:+e.target.value})}/></div>
                      <div className="fg"><span className="fl new">Ply To</span><input type="number" className="inp new-field" style={{width:90}} value={layForm.plyTo} onChange={e=>setLF({...layForm,plyTo:+e.target.value})}/></div>
                      <div className="fg"><span className="fl new">Cut Qty ★</span><input type="number" className="inp new-field" style={{width:100}} value={layForm.cutQty} onChange={e=>setLF({...layForm,cutQty:+e.target.value})}/></div>
                      <div className="fg"><span className="fl new">Bundle Size ★</span><input type="number" className="inp new-field" style={{width:100}} value={layForm.bundleQty} onChange={e=>setLF({...layForm,bundleQty:+e.target.value})}/></div>
                      <div className="fg">
                        <span className="fl new">Size ★ <SrcBadge src="NIRVIKALP" /></span>
                        <select className="sel new-field" value={layForm.size} onChange={e=>setLF({...layForm,size:e.target.value})}>
                          <option value="">—</option>
                          {styleData.sizes.map(s=><option key={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="fg">
                        <span className="fl new">Color ★ <SrcBadge src="NIRVIKALP" /></span>
                        <select className="sel new-field" value={layForm.color} onChange={e=>setLF({...layForm,color:e.target.value})}>
                          <option value="">—</option>
                          {styleData.colors.map(c=><option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <button className="btn btn-g" style={{alignSelf:'flex-end'}} onClick={()=>addLay(plan.id)} disabled={saving}>
                        {saving?<span className="spin"/>:null} ➕ Add Lay + Gen BCs
                      </button>
                    </div>
                    <div style={{fontSize:11,color:'#92400e'}}>
                      Will create: {Math.ceil(layForm.cutQty/layForm.bundleQty)||0} bundles × {styleData.opCount} op-BCs + {layForm.cutQty} product QRs
                      = <strong>{(Math.ceil(layForm.cutQty/layForm.bundleQty)||0)*styleData.opCount + layForm.cutQty}</strong> total barcodes
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {sub === 'lays' && (
          <div className="card">
            <h3>📐 Lay Details</h3>
            {(pmDb.lays||[]).length === 0
              ? <div className="empty-st"><div className="et">No lays yet. Create a cut plan and add lays.</div></div>
              : (
                <table className="tbl">
                  <thead><tr><th>Plan</th><th>Lay</th><th>Size</th><th>Color</th><th>Plies</th><th>Cut Qty</th><th>Bundle Sz</th><th>Bundles</th><th>Op BCs</th><th>Product QRs</th></tr></thead>
                  <tbody>
                    {(pmDb.lays||[]).map(lay=>{
                      const plan = (pmDb.cutPlans||[]).find(p=>p.id===lay.plan_id);
                      const lBundles = (pmDb.bundles||[]).filter(b=>b.plan_id===lay.plan_id&&b.lay_no===lay.layNo);
                      const lOpBCs   = (pmDb.barcodes||[]).filter(b=>b.plan_id===lay.plan_id&&b.lay_no===lay.layNo&&b.barcode_type==='OPERATION');
                      const lPrdQRs  = (pmDb.barcodes||[]).filter(b=>b.plan_id===lay.plan_id&&b.lay_no===lay.layNo&&b.barcode_type==='PRODUCT');
                      return (
                        <tr key={lay.id}>
                          <td>{plan?.style_ref||lay.plan_id}</td>
                          <td>Lay {lay.layNo}</td>
                          <td><span className="pill pill-a">{lay.size}</span></td>
                          <td>{lay.color||'—'}</td>
                          <td>{lay.plyFrom}–{lay.plyTo}</td>
                          <td><strong>{lay.cutQty}</strong></td>
                          <td>{lay.bundleQty}</td>
                          <td><span className="pill pill-g">{lBundles.length}</span></td>
                          <td><span className="pill pill-2">{lOpBCs.length}</span></td>
                          <td><span className="pill pill-w">{lPrdQRs.length}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            }
          </div>
        )}

        {sub === 'barcodes' && (
          <div className="card">
            <h3>🔷 Generated Barcodes
              <button className="btn btn-s no-print" style={{marginLeft:'auto',fontSize:12}} onClick={()=>window.print()}>🖨 Print All</button>
            </h3>
            {(pmDb.barcodes||[]).length === 0
              ? <div className="empty-st"><div className="et">No barcodes yet. Add lays to generate.</div></div>
              : (
                <>
                  <Stats items={[
                    { label:'Operation BCs', val:(pmDb.barcodes||[]).filter(b=>b.barcode_type==='OPERATION').length, color:'#1565C0' },
                    { label:'Product QRs',   val:(pmDb.barcodes||[]).filter(b=>b.barcode_type==='PRODUCT').length,   color:'#6A1B9A' },
                    { label:'Total',         val:(pmDb.barcodes||[]).length },
                  ]} />
                  <div style={{fontWeight:600,marginBottom:8}}>Operation Barcodes (Code128)</div>
                  <div className="bc-grid">
                    {(pmDb.barcodes||[]).filter(b=>b.barcode_type==='OPERATION').slice(0,30).map((b,i)=>(
                      <BarcodeCard key={i} label={`${b.op_name||b.op_code}`} subLabel={`Bundle ${b.bundle_no} · L${b.lay_no}`} value={b.barcode} type="barcode" />
                    ))}
                  </div>
                  <div style={{fontWeight:600,margin:'16px 0 8px'}}>Product QR Codes</div>
                  <div className="bc-grid">
                    {(pmDb.barcodes||[]).filter(b=>b.barcode_type==='PRODUCT').slice(0,20).map((b,i)=>(
                      <BarcodeCard key={i} label={`Garment ${b.garment_no}`} subLabel={`Bundle ${b.bundle_no}`} value={b.qr} type="qr" />
                    ))}
                  </div>
                </>
              )
            }
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 3. PRINT & SCAN ─────────────────────────────────────────────────────────
function PrintScan({ db, pmDb, setPmDb, apiLive }) {
  const [sub, setSub]   = useState('periods');
  const [alert, setA]   = useState(null);
  const [saving, setSv] = useState(false);
  const [scanStep, setScanStep] = useState('period');
  const [ctx, setCtx]   = useState({ period:null, line:null, operator:null });
  const [scanLog, setLog] = useState([]);
  const [manualBC, setMBC] = useState('');
  const scanRef = useRef(null);

  const [pf, setPF] = useState({ date:new Date().toISOString().slice(0,10), workHrs:10, durHrs:2, lineId:'' });

  const allLines = bridge.getAllLines(db);
  const netMin   = eff.netMin(pf.workHrs, 30, 10);
  const nPer     = eff.periods(pf.workHrs, pf.durHrs, 40);

  const genPeriods = async () => {
    if (!pf.lineId) return setA({ msg:'Select a line', type:'err' });
    setSv(true);
    const perMin = Math.floor(pf.durHrs * 60);
    const periods = Array.from({length:nPer}, (_, i) => ({
      id: `${pf.date}-L${pf.lineId}-P${i+1}`,
      date: pf.date, line_id: pf.lineId, period_no: i+1,
      start_min: i*perMin, end_min: (i+1)*perMin, duration_min: perMin,
      barcode: BC.period(pf.date, pf.lineId, i+1),
      source_flag: 'LOCAL',
    }));
    const upd = { ...pmDb, periods:[...(pmDb.periods||[]).filter(p=>!(p.date===pf.date&&p.line_id===pf.lineId)), ...periods] };
    setPmDb(upd); LS.set('periods', upd.periods);
    if (apiLive) await pmApi.batchSave('pm_period', periods);
    setSv(false);
    setA({ msg:`${nPer} periods generated for ${pf.date}`, type:'ok' });
  };

  const todayPeriods = (pmDb.periods||[]).filter(p=>p.date===pf.date&&p.line_id===pf.lineId);

  // Scan processing
  const processScan = async (val) => {
    const v = val.trim(); if (!v) return;
    const period = (pmDb.periods||[]).find(p=>p.barcode===v);
    const line   = (pmDb.lineConfigs||[]).find(l=>l.barcode===v);
    const worker = (pmDb.workers||[]).find(w=>w.worker_barcode===v);
    const bundle = (pmDb.bundles||[]).find(b=>b.barcode===v);
    const opBC   = (pmDb.barcodes||[]).find(b=>b.barcode===v&&b.barcode_type==='OPERATION');

    let entry = { type:'UNKNOWN', val:v, ts:Date.now() };

    if (period && scanStep==='period') {
      entry = { type:'PERIOD', val:v, data:period, ok:true, ts:Date.now() };
      setCtx({...ctx, period}); setScanStep('line');
    } else if (line && scanStep==='line') {
      entry = { type:'LINE', val:v, data:line, ok:true, ts:Date.now() };
      setCtx({...ctx, line}); setScanStep('operator');
    } else if (worker && scanStep==='operator') {
      entry = { type:'OPERATOR', val:v, data:worker, ok:true, ts:Date.now() };
      setCtx({...ctx, operator:worker}); setScanStep('bundle');
    } else if ((bundle||opBC) && scanStep==='bundle') {
      const smvMin = opBC ? parseFloat(opBC.smv)||0 : 0;
      const rec = {
        id: Date.now(), period_id:ctx.period?.id, line_id:ctx.line?.line_id,
        factory_id:ctx.line?.factory_id, worker_id:ctx.operator?.id,
        operator_id:ctx.operator?.operator_id, bundle_id:bundle?.id||opBC?.bundle_id,
        op_code:opBC?.op_code, op_name:opBC?.op_name, smv_minutes:smvMin,
        barcode:v, scanned_at:new Date().toISOString(), source:'LIVE',
      };
      const upd = { ...pmDb, scanRecords:[...(pmDb.scanRecords||[]),rec] };
      setPmDb(upd); LS.set('scanRecords',upd.scanRecords);
      if (apiLive) await pmApi.save('pm_scan_record', rec);
      entry = { type:opBC?'OPERATION':'BUNDLE', val:v, data:rec, ok:true, ts:Date.now() };
    } else {
      entry = { type:'UNKNOWN', val:v, error:`Expected: ${scanStep}`, ok:false, ts:Date.now() };
    }
    setLog(l=>[entry,...l].slice(0,100));
    setMBC('');
  };

  const exportCSV = () => {
    const recs = pmDb.scanRecords||[];
    const csv  = ['period_id,line_id,operator_id,bundle_id,op_code,smv_minutes,scanned_at',
      ...recs.map(r=>`${r.period_id||''},${r.line_id||''},${r.operator_id||''},${r.bundle_id||''},${r.op_code||''},${r.smv_minutes||0},${r.scanned_at}`)
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = `scan_${pf.date}.csv`; a.click();
  };

  const importCSV = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const rows = ev.target.result.split('\n').slice(1).filter(l=>l.trim());
      const imported = rows.map(l=>{
        const [period_id,line_id,operator_id,bundle_id,op_code,smv_minutes,scanned_at] = l.split(',');
        return { id:Date.now()+Math.random(), period_id,line_id,operator_id,bundle_id,op_code,smv_minutes:parseFloat(smv_minutes)||0,scanned_at,source:'IMPORTED' };
      });
      const upd = { ...pmDb, scanRecords:[...(pmDb.scanRecords||[]),...imported] };
      setPmDb(upd); LS.set('scanRecords',upd.scanRecords);
      if (apiLive) await pmApi.batchSave('pm_scan_record', imported);
      setA({ msg:`${imported.length} records imported`, type:'ok' });
    };
    reader.readAsText(file);
    e.target.value='';
  };

  const scanSteps = [
    { key:'period',   label:'Period BC',      cls:'s-per'  },
    { key:'line',     label:'Line BC',        cls:'s-line' },
    { key:'operator', label:'Operator BC',    cls:'s-op'   },
    { key:'bundle',   label:'Bundle / Op BC', cls:'s-bnd'  },
  ];

  return (
    <div className="layout-master">
      <div className="side-rail">
        <div className="side-hdr">Print & Scan</div>
        {[['periods','⏱ Period Setup'],['printBarcodes','🖨 Print All'],['scan','📷 Live Scan'],['scanFile','📥 Scan File']].map(([k,l])=>(
          <button key={k} className={`side-btn ${sub===k?'on':''}`} onClick={()=>setSub(k)}>{l}</button>
        ))}
        {/* Scan context */}
        <div style={{padding:10,borderTop:'1px solid #DEE2E6',marginTop:'auto'}}>
          <div style={{fontSize:11,fontWeight:700,color:'#6C757D',marginBottom:6}}>SCAN STATE</div>
          {scanSteps.map(s=>(
            <div key={s.key} style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
              <span className={`scan-nd ${s.cls} ${scanStep===s.key?'active':''} ${ctx[s.key]?'done':''}`} style={{fontSize:10,padding:'2px 8px'}}>
                {ctx[s.key]?'✓ ':''}{s.label}
              </span>
            </div>
          ))}
          <button className="btn btn-s" style={{width:'100%',marginTop:8,fontSize:12}}
            onClick={()=>{ setScanStep('period'); setCtx({period:null,line:null,operator:null}); setLog([]); }}>
            🔄 Reset Scan
          </button>
        </div>
      </div>

      <div className="content-area">
        {alert && <Alert msg={alert.msg} type={alert.type} onClose={()=>setA(null)} />}

        {/* Period setup */}
        {sub==='periods' && (
          <div className="card">
            <h3>⏱ Scan Period Setup</h3>
            <div className="new-block">
              <div className="block-hdr">⚠️ New Entry — period barcodes generated here</div>
              <div className="form-row">
                <div className="fg"><span className="fl new">Date ★</span><input type="date" className="inp new-field" style={{width:160}} value={pf.date} onChange={e=>setPF({...pf,date:e.target.value})}/></div>
                <div className="fg"><span className="fl new">Working Hours ★</span><input type="number" className="inp new-field" style={{width:130}} value={pf.workHrs} onChange={e=>setPF({...pf,workHrs:+e.target.value})}/></div>
                <div className="fg"><span className="fl new">Period Duration (hr) ★</span><input type="number" step="0.5" className="inp new-field" style={{width:140}} value={pf.durHrs} onChange={e=>setPF({...pf,durHrs:+e.target.value})}/></div>
                <div className="fg"><span className="fl new">Line ★</span>
                  <select className="sel new-field" value={pf.lineId} onChange={e=>setPF({...pf,lineId:e.target.value})}>
                    <option value="">— Select Line —</option>
                    {allLines.map(l=><option key={`${l.factoryId}-${l.id}`} value={l.id}>{l.factoryName} · {l.name||l.category||`Line ${l.id}`}</option>)}
                  </select>
                </div>
                <button className="btn btn-p" style={{alignSelf:'flex-end'}} onClick={genPeriods} disabled={saving}>
                  {saving?<span className="spin"/>:null} Generate
                </button>
              </div>
              <div style={{background:'#FFE082',padding:10,borderRadius:6,fontSize:13}}>
                {pf.workHrs}hr × 60 = {pf.workHrs*60}min − 40min breaks = {netMin}min net ÷ {Math.floor(pf.durHrs*60)}min/period = <strong>{nPer} periods</strong>
              </div>
            </div>

            {todayPeriods.length > 0 && (
              <>
                <div style={{fontWeight:600,marginBottom:12}}>Generated Period Barcodes</div>
                <div className="bc-grid">
                  {todayPeriods.map(p=>(
                    <BarcodeCard key={p.id} label={`Period ${p.period_no}`} subLabel={`${p.duration_min} min · ${p.date}`} value={p.barcode} type="qr" />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Print all barcodes */}
        {sub==='printBarcodes' && (
          <>
            <div className="card">
              <h3>🖨 Print Barcodes
                <button className="btn btn-p no-print" style={{marginLeft:'auto'}} onClick={()=>window.print()}>🖨 Print Page</button>
              </h3>

              <div style={{fontWeight:600,marginBottom:8}}>Periods ({(pmDb.periods||[]).length})</div>
              <div className="bc-grid">
                {(pmDb.periods||[]).map(p=><BarcodeCard key={p.id} label={`Period ${p.period_no}`} subLabel={p.date} value={p.barcode} type="qr"/>)}
              </div>

              <div style={{fontWeight:600,margin:'16px 0 8px'}}>Production Lines ({(pmDb.lineConfigs||[]).length})</div>
              <div className="bc-grid">
                {(pmDb.lineConfigs||[]).map(l=><BarcodeCard key={l.line_id} label={l.line_name} subLabel={l.factory_name} value={l.barcode} type="barcode"/>)}
              </div>

              <div style={{fontWeight:600,margin:'16px 0 8px'}}>Workers ({(pmDb.workers||[]).length})</div>
              <div className="bc-grid">
                {(pmDb.workers||[]).map(w=><BarcodeCard key={w.id} label={w.name} subLabel={`${w.operator_id} · ${w.designation}`} value={w.worker_barcode} type="barcode"/>)}
              </div>

              <div style={{fontWeight:600,margin:'16px 0 8px'}}>Bundle Barcodes (Operation) — showing first 30</div>
              <div className="bc-grid">
                {(pmDb.barcodes||[]).filter(b=>b.barcode_type==='OPERATION').slice(0,30).map((b,i)=>(
                  <BarcodeCard key={i} label={b.op_name||b.op_code} subLabel={`Lay ${b.lay_no} · Bundle ${b.bundle_no}`} value={b.barcode} type="barcode"/>
                ))}
              </div>

              <div style={{fontWeight:600,margin:'16px 0 8px'}}>Product QR Codes — showing first 20</div>
              <div className="bc-grid">
                {(pmDb.barcodes||[]).filter(b=>b.barcode_type==='PRODUCT').slice(0,20).map((b,i)=>(
                  <BarcodeCard key={i} label={`Garment ${b.garment_no}`} subLabel={`Bundle ${b.bundle_no}`} value={b.qr} type="qr"/>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Live scan */}
        {sub==='scan' && (
          <div className="card">
            <h3>📷 Live Barcode Scanning</h3>
            <div className="scan-seq">
              {scanSteps.map((s,i)=>(
                <React.Fragment key={s.key}>
                  <span className={`scan-nd ${s.cls} ${scanStep===s.key?'active':''} ${ctx[s.key]?'done':''}`}>
                    {ctx[s.key]?'✓ ':''}{s.label}
                  </span>
                  {i<scanSteps.length-1 && <span className="scan-arrow">→</span>}
                </React.Fragment>
              ))}
            </div>

            <Stats items={[
              { label:'Step', val:scanStep.toUpperCase(), color:'#1565C0' },
              { label:'Period', val:ctx.period?.period_no||'—' },
              { label:'Line', val:ctx.line?.line_name||'—' },
              { label:'Operator', val:ctx.operator?.name||'—' },
            ]} />

            <div className="form-row">
              <div className="fg" style={{flex:1}}>
                <span className="fl">Scan barcode or type + Enter</span>
                <input ref={scanRef} className="inp" style={{width:'100%',fontSize:16,padding:'10px 14px'}}
                  placeholder={`Waiting for ${scanStep} barcode...`}
                  value={manualBC} onChange={e=>setMBC(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')processScan(manualBC);}} autoFocus/>
              </div>
              <button className="btn btn-p" style={{alignSelf:'flex-end'}} onClick={()=>processScan(manualBC)}>Scan</button>
            </div>

            <table className="tbl">
              <thead><tr><th>Type</th><th>Barcode</th><th>Status</th><th>Time</th></tr></thead>
              <tbody>
                {scanLog.slice(0,20).map((l,i)=>(
                  <tr key={i}>
                    <td><span className={`pill ${l.ok?'pill-g':'pill-r'}`}>{l.type}</span></td>
                    <td style={{fontFamily:'monospace',fontSize:12}}>{l.val}</td>
                    <td>{l.ok ? <span className="pill pill-g">OK</span> : <span className="pill pill-r">{l.error}</span>}</td>
                    <td style={{fontFamily:'monospace',fontSize:11}}>{new Date(l.ts).toLocaleTimeString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Scan file */}
        {sub==='scanFile' && (
          <div className="card">
            <h3>📥 Scan File Import / Export</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:16}}>
              <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:8,padding:16}}>
                <div style={{fontWeight:700,marginBottom:8,color:'#15803d'}}>📤 Export</div>
                <div style={{fontSize:13,color:'#6C757D',marginBottom:12}}>Download scan records as CSV for backup or handover</div>
                <button className="btn btn-g" onClick={exportCSV}>⬇ Export CSV</button>
                <div style={{fontSize:11,color:'#6C757D',marginTop:8}}>Format: period_id, line_id, operator_id, bundle_id, op_code, smv_minutes, scanned_at</div>
              </div>
              <div style={{background:'#eff6ff',border:'1px solid #90CAF9',borderRadius:8,padding:16}}>
                <div style={{fontWeight:700,marginBottom:8,color:'#0D47A1'}}>📥 Import</div>
                <div style={{fontSize:13,color:'#6C757D',marginBottom:12}}>Import scan CSV from handheld scanner — mirrors sp_ReceiveScannedFile</div>
                <label className="btn btn-p" style={{cursor:'pointer'}}>
                  ⬆ Import CSV <input type="file" accept=".csv" style={{display:'none'}} onChange={importCSV}/>
                </label>
              </div>
            </div>

            <div style={{fontWeight:600,marginBottom:8}}>Scan Records: {(pmDb.scanRecords||[]).length}</div>
            {(pmDb.scanRecords||[]).length > 0 && (
              <table className="tbl">
                <thead><tr><th>Period</th><th>Operator</th><th>Bundle</th><th>Operation</th><th>SMV min</th><th>Source</th><th>Scanned At</th></tr></thead>
                <tbody>
                  {(pmDb.scanRecords||[]).slice(-30).reverse().map((r,i)=>(
                    <tr key={i}>
                      <td style={{fontFamily:'monospace',fontSize:11}}>{(r.period_id||'').slice(-8)||'—'}</td>
                      <td>{r.operator_id||'—'}</td>
                      <td style={{fontFamily:'monospace',fontSize:11}}>{(r.bundle_id||'').slice(-10)||'—'}</td>
                      <td>{r.op_name||r.op_code||'—'}</td>
                      <td>{r.smv_minutes||0}</td>
                      <td><SrcBadge src={r.source==='IMPORTED'?'IMPORTED':r.source==='LIVE'?'LOCAL':'LOCAL'}/></td>
                      <td style={{fontSize:11}}>{r.scanned_at?new Date(r.scanned_at).toLocaleString():'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 4. MIS REPORTS ──────────────────────────────────────────────────────────
const MIS_REPORTS = [
  { code:'OAP',  label:'Overall Performance',          group:'Performance', sp:'sp_FactoryEfficiencyReport'        },
  { code:'SE',   label:'Section Performance',           group:'Performance', sp:'sp_SectionEfficiencyReport'        },
  { code:'SECT', label:'Factory / Section Detail',      group:'Performance', sp:'sp_MisReport (SECT)'               },
  { code:'STM',  label:'Style Performance (Min-wise)',  group:'Performance', sp:'sp_StyelSummaryReport'             },
  { code:'OR',   label:'Order Performance',             group:'Performance', sp:'sp_ScanningReports'                },
  { code:'ORM',  label:'Order Performance (Min-wise)',  group:'Performance', sp:'sp_MonthlySummaryReport'           },
  { code:'SWIP', label:'Section Wise WIP',              group:'WIP',         sp:'sp_MisReport (SWIP)'               },
  { code:'OWP',  label:'Order Wise Part Performance',   group:'WIP',         sp:'sp_PartoutputWiseReport'           },
  { code:'PWP',  label:'Part Wise Performance',         group:'WIP',         sp:'sp_PartoutputWiseReport'           },
  { code:'OPEFF',label:'Operator Efficiency (Date)',    group:'Operator',    sp:'sp_OperatorEfficiencyByPeriod'     },
  { code:'OPEFP',label:'Operator Efficiency (Period)',  group:'Operator',    sp:'sp_OperatorEfficiencyByPeriod'     },
  { code:'OCSWD',label:'Order Color / Size Details',    group:'Details',     sp:'sp_StyleSummaryBySection'         },
  { code:'SCSWD',label:'Style Color / Size Details',    group:'Details',     sp:'sp_StyelSummaryReportForSection'  },
];
const MIS_GRP = ['Performance','WIP','Operator','Details'];

function MISReports({ db, pmDb, apiLive }) {
  const [code, setCode] = useState('');
  const [from, setFrom] = useState(new Date().toISOString().slice(0,8)+'01');
  const [to,   setTo]   = useState(new Date().toISOString().slice(0,10));
  const [line, setLine] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const rpt = MIS_REPORTS.find(r=>r.code===code);
  const rows = result||[];
  const avgEff = rows.length && rows[0]?.['Efficiency%']!==undefined
    ? Math.round(rows.reduce((s,r)=>s+(+r['Efficiency%']||0),0)/rows.length) : null;

  const run = useCallback(async () => {
    if (!code) return;
    setLoading(true); setResult(null);

    // 1. Try pm_api.php (includes DataPad bridge)
    if (apiLive) {
      try {
        const apiRows = await pmApi.mis(code, from, to, line);
        if (apiRows && apiRows.length > 0) { setResult(apiRows); setLoading(false); return; }
      } catch(e) {}
    }

    // 2. Build combined scan records: PM records + DataPad bridge
    const pmRecs = (pmDb.scanRecords || []).filter(r => {
      const d = (r.scanned_at || r.date || '').slice(0,10);
      return (!from || d >= from) && (!to || d <= to) && (!line || r.line_id === line);
    });
    const prodLogs = (db?.productionLogs || []).filter(log => {
      return log.date && (!from || log.date >= from) && (!to || log.date <= to)
        && (!line || log.line === line || log.line_id === line);
    });
    const bridged = prodLogs.map(log => ({
      id: 'dp_'+(log.id||Math.random()),
      source: 'DATAPAD',
      line_id: log.line || log.line_id || log.section || 'Floor',
      operator_id: log.operator || log.operator_id || '',
      smv_minutes: parseFloat(log.smv || log.smv_per_pc || 0),
      output_qty: parseInt(log.output || log.pieces || log.qty || 0),
      scanned_at: (log.date||'')+'T00:00:00',
      period_id: log.date || '',
      bundle_id: null,
    }));
    const existIds = new Set(pmRecs.map(r=>r.id));
    const recs = [...pmRecs, ...bridged.filter(r=>!existIds.has(r.id))];

    const workers  = pmDb.workers      || [];
    const bundles  = pmDb.bundles      || [];
    const barcodes = pmDb.barcodes     || [];
    const lines    = pmDb.lineConfigs  || [];
    const avail    = pmDb.availableTimes || [];

    const getAvailMin = lineId => {
      const at = avail.find(a=>a.lineId===lineId||a.line_id===lineId);
      return parseInt(at?.net_minutes||at?.netMinutes||480);
    };
    const getWorkers = lineId => {
      const lc = lines.find(l=>l.line_id===lineId);
      return workers.filter(w=>w.line_id===lineId).length || parseInt(lc?.workers_count||lc?.workers||20);
    };
    const calcEff = (output, smvTotal, availMin, wkrs) => {
      const capacity = Math.max(availMin * wkrs, 1);
      return Math.min(Math.round(smvTotal / capacity * 100), 200);
    };

    const src = pmRecs.length>0&&bridged.length>0
      ? 'PM records + DataPad' : pmRecs.length>0 ? 'PM scan records'
      : bridged.length>0 ? 'DataPad only (pm_api.php offline)' : '';

    if (!recs.length) {
      setResult([{ Info: src||'No data', Detail:'No production entries found for the selected date range.', 'PM Records':pmRecs.length, 'DataPad Records':bridged.length }]);
      setLoading(false); return;
    }

    // ── Section / Line efficiency ─────────────────────────────────────────────
    if (['OAP','SE','SECT','EFF'].includes(code)) {
      const byLine = {};
      recs.forEach(r => {
        const k = r.line_id || 'Floor';
        if (!byLine[k]) byLine[k] = { scans:0, smv:0, output:0, lineId:k };
        byLine[k].scans++;
        byLine[k].smv    += parseFloat(r.smv_minutes||0);
        byLine[k].output += parseInt(r.output_qty||1);
      });
      const res = Object.values(byLine).map(bl => {
        const aMin = getAvailMin(bl.lineId);
        const wkrs = getWorkers(bl.lineId);
        const lc   = lines.find(l=>l.line_id===bl.lineId);
        const out  = bl.output > 0 ? bl.output : bl.scans;
        const smvT = bl.smv > 0 ? bl.smv : out * (bl.smv/Math.max(bl.scans,1));
        return {
          Section: lc?.line_name||bl.lineId, Output:out, Scans:bl.scans,
          'SMV Total': bl.smv.toFixed(1), 'Avail Min':aMin, Workers:wkrs,
          'Efficiency%': calcEff(out, bl.smv, aMin, wkrs),
          'Data Source': src,
        };
      }).sort((a,b)=>b['Efficiency%']-a['Efficiency%']);
      setResult(res);

    // ── Operator efficiency ───────────────────────────────────────────────────
    } else if (['OPEFF','OPEFP','OWP'].includes(code)) {
      const byOp = {};
      recs.forEach(r => {
        const k = r.operator_id || 'Unknown';
        if (!byOp[k]) byOp[k] = { scans:0, smv:0, output:0, periods:new Set() };
        byOp[k].scans++;
        byOp[k].smv    += parseFloat(r.smv_minutes||0);
        byOp[k].output += parseInt(r.output_qty||1);
        if (r.period_id) byOp[k].periods.add(r.period_id);
      });
      const res = Object.entries(byOp).map(([opId,d]) => {
        const w   = workers.find(wk=>wk.operator_id===opId||wk.id===opId);
        const dur = Math.max(d.periods.size * 120, 480);
        const out = d.output > 0 ? d.output : d.scans;
        return {
          'Operator': w?.name||opId, 'Operator ID':opId,
          'Output': out, 'Scans': d.scans,
          'SMV Total': d.smv.toFixed(2), 'Days': d.periods.size,
          'Efficiency%': calcEff(out, d.smv, dur, 1),
          'Data Source': src,
        };
      }).sort((a,b)=>b['Efficiency%']-a['Efficiency%']);
      setResult(res);

    // ── WIP / Bundle status ───────────────────────────────────────────────────
    } else if (code==='SWIP') {
      const res = bundles.map(b => {
        const tot = barcodes.filter(bc=>bc.bundle_id===b.id&&bc.barcode_type==='OPERATION').length;
        const scn = recs.filter(r=>r.bundle_id===b.id).length;
        const pct = tot>0?Math.round(scn/tot*100):0;
        return {
          Bundle:(b.id||'').toString().slice(-10), Lay:b.lay_no,
          'Bundle No':b.bundle_no, Size:b.size||'—', Color:b.color||'—',
          Qty:b.qty, 'Total Ops':tot, Scanned:scn, WIP:Math.max(0,tot-scn),
          'Progress%':pct, Status:scn>=tot&&tot>0?'Complete':scn>0?'In Progress':'Pending',
        };
      });
      setResult(res.length ? res : [{ Info:'No bundles yet. Create bundles in Cut Plan tab.' }]);

    // ── Style-wise output ─────────────────────────────────────────────────────
    } else if (['OWP','PWP','SWP','SPD'].includes(code)) {
      const byStyle = {};
      recs.forEach(r => {
        const k = r.style || 'Unknown';
        if (!byStyle[k]) byStyle[k] = { output:0, smv:0, scans:0 };
        byStyle[k].output += parseInt(r.output_qty||1);
        byStyle[k].smv    += parseFloat(r.smv_minutes||0);
        byStyle[k].scans++;
      });
      const res = Object.entries(byStyle).map(([style,d]) => ({
        Style: style, Output: d.output||d.scans,
        'SMV Total': d.smv.toFixed(1),
        'Avg SMV': d.scans>0?(d.smv/d.scans).toFixed(3):'—',
      })).sort((a,b)=>b.Output-a.Output);
      setResult(res);

    } else {
      // Generic: show raw aggregation
      const byLine = {};
      recs.forEach(r=>{
        const k=r.line_id||'Floor';
        if(!byLine[k]) byLine[k]={output:0,scans:0,smv:0};
        byLine[k].scans++; byLine[k].output+=parseInt(r.output_qty||1); byLine[k].smv+=parseFloat(r.smv_minutes||0);
      });
      setResult(Object.entries(byLine).map(([k,d])=>({
        Section:k, Output:d.output||d.scans, 'SMV Total':d.smv.toFixed(1), 'Source':src,
      })));
    }
    setLoading(false);
  }, [code, from, to, line, pmDb, db, apiLive]);

  return (
    <div className="layout-master">
      <div className="side-rail">
        <div className="side-hdr">MIS Reports</div>
        <div className="mis-list" style={{flex:1,overflowY:'auto'}}>
          {MIS_GRP.map(g=>(
            <div key={g}>
              <div style={{fontSize:10,color:'#6C757D',fontWeight:700,letterSpacing:1,textTransform:'uppercase',padding:'10px 8px 4px'}}>{g}</div>
              {MIS_REPORTS.filter(r=>r.group===g).map(r=>(
                <button key={r.code} className={code===r.code?'on':''} onClick={()=>setCode(r.code)}>
                  <span className="mis-code">{r.code}</span>{r.label}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div style={{padding:12,borderTop:'1px solid #DEE2E6'}}>
          <button className="btn btn-p" style={{width:'100%'}} onClick={run} disabled={!code||loading}>
            {loading?<span className="spin"/>:null} {loading?'Running…':'▶ Run'}
          </button>
        </div>
      </div>
      <div className="content-area">
        <div className="card" style={{marginBottom:12}}>
          <div className="form-row" style={{marginBottom:0}}>
            <div className="fg"><span className="fl">From</span><input type="date" className="inp" style={{width:160}} value={from} onChange={e=>setFrom(e.target.value)}/></div>
            <div className="fg"><span className="fl">To</span><input type="date" className="inp" style={{width:160}} value={to} onChange={e=>setTo(e.target.value)}/></div>
            <div className="fg"><span className="fl">Line</span>
              <select className="sel" value={line} onChange={e=>setLine(e.target.value)}>
                <option value="">All Lines</option>
                {(pmDb.lineConfigs||[]).map(l=><option key={l.line_id} value={l.line_id}>{l.line_name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {rows.length>0 && (
          <Stats items={[
            { label:'Records', val:rows.length, color:'#1565C0' },
            ...(avgEff!==null?[{ label:'Avg Efficiency', val:`${avgEff}%`, color:avgEff>=90?'#388E3C':avgEff>=70?'#E65100':'#C62828', sub:avgEff>=90?'On Target':avgEff>=70?'Needs Attention':'Below Target' }]:[]),
            { label:'Report', val:code, color:'#6A1B9A' },
            { label:'Source', val:apiLive?'API':'Local' },
          ]} />
        )}

        {rpt && (loading||result) && (
          <div style={{marginBottom:10,fontSize:12,color:'#6C757D'}}>
            Procedure: <code style={{background:'#F4F6F8',padding:'2px 6px',borderRadius:4}}>{rpt.sp}</code>
          </div>
        )}

        {!loading&&!result&&<div className="empty-st"><div className="ei">📊</div><div className="et">Select a report and click Run</div></div>}
        {loading&&<div className="empty-st"><div className="spin" style={{marginBottom:10}}/><div className="et">Running {rpt?.sp}…</div></div>}

        {!loading&&rows.length>0&&(
          <div className="card">
            <h3>{rpt?.label}<span style={{marginLeft:'auto',fontSize:11,color:'#6C757D',fontFamily:'monospace'}}>{rows.length} rows</span></h3>
            <div style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead><tr>{Object.keys(rows[0]).map(k=><th key={k}>{k}</th>)}</tr></thead>
                <tbody>{rows.map((row,i)=>(
                  <tr key={i}>{Object.entries(row).map(([k,v])=>(
                    <td key={k}>
                      {k==='Efficiency%'?<span className={`pill ${+v>=90?'pill-g':+v>=70?'pill-w':'pill-r'}`}>{v}%</span>:
                       k==='Status'?<span className={`pill ${v==='Complete'?'pill-g':'pill-w'}`}>{v}</span>:
                       String(v)}
                    </td>
                  ))}</tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────
const TABS = [
  { key:'factory',   label:'🏭 Factory Profile' },
  { key:'scanmodule', label:'📦 Production Scan' },
  { key:'mis',       label:'📊 MIS Reports'       },
  { key:'lean',      label:'📊 Lean Management'   },
];

export default function ProductionMonitor({ db, setDb, saveToDB }) {
  const [tab, setTab]       = useState('factory');
  const [apiStatus, setApi] = useState('checking');

  // PM database — API + localStorage
  const [pmDb, setPmDb] = useState(() => ({
    lineConfigs:    LS.get('lineConfigs')    || [],
    workers:        LS.get('workers')        || [],
    availableTimes: LS.get('availableTimes') || [],
    cutPlans:       LS.get('cutPlans')       || [],
    lays:           LS.get('lays')           || [],
    bundles:        LS.get('bundles')        || [],
    barcodes:       LS.get('barcodes')       || [],
    periods:        LS.get('periods')        || [],
    scanRecords:    LS.get('scanRecords')    || [],
  }));

  // Check API + load PM data
  useEffect(() => {
    const init = async () => {
      const live = await pmApi.checkAvailable();
      setApi(live ? 'live' : 'local');
      if (live) {
        const data = await pmApi.loadAll();
        if (data) {
          setPmDb(prev => ({
            ...prev,
            lineConfigs:    data.lineConfigs    || prev.lineConfigs,
            workers:        data.workers        || prev.workers,
            availableTimes: data.availableTimes || prev.availableTimes,
            cutPlans:       data.cutPlans       || prev.cutPlans,
            lays:           data.lays           || prev.lays,
            bundles:        data.bundles        || prev.bundles,
            periods:        data.periods        || prev.periods,
            scanRecords:    data.scanRecords    || prev.scanRecords,
          }));
        }
      }
    };
    init();
  }, []);

  const apiLive = apiStatus === 'live';

  // ── REAL-TIME SYNC — listens for updates from Production Planning ────────────
  useEffect(() => {
    const handleUpdate = (e) => {
      const { type, data } = e.detail || {};
      if (type === 'PLAN_SAVED' || type === 'CUTPLAN_SAVED') {
        // Reload PM data from API to get latest
        pmApi.loadAll().then(fresh => {
          if (fresh) setPmDb(prev => ({
            ...prev,
            cutPlans: fresh.cutPlans || prev.cutPlans,
            lays: fresh.lays || prev.lays,
            bundles: fresh.bundles || prev.bundles,
          }));
        }).catch(() => {});
      }
    };
    window.addEventListener('nirvikalp_update', handleUpdate);
    return () => window.removeEventListener('nirvikalp_update', handleUpdate);
  }, []);


  return (
    <>
      <style>{css}</style>
      <div className="pm">
        <div className="pm-top">
          <span className="pm-logo">PM</span>
          <div className="pm-sep"/>
          <span className="pm-sub">Production Monitoring · Nirvikalp Extension</span>
          <div className="pm-sp"/>
          <span className={`conn-badge ${apiStatus}`}>
            {apiStatus==='checking'?'⟳ Checking API…':apiStatus==='live'?'● API Live':'○ Local Mode (localStorage)'}
          </span>
        </div>

        <div className="pm-tabs">
          {TABS.map(t=>(
            <button key={t.key} className={`pm-tab ${tab===t.key?'on':''}`} onClick={()=>setTab(t.key)}>{t.label}</button>
          ))}
        </div>

        <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>
          {tab==='factory'   && <FactoryProfile db={db} pmDb={pmDb} setPmDb={setPmDb} apiLive={apiLive} />}
          {tab==='scanmodule' && <ScanModule db={db} pmDb={pmDb} setPmDb={setPmDb} apiLive={apiLive} />}
          {tab==='mis'       && <MISReports     db={db} pmDb={pmDb} apiLive={apiLive} />}
          {tab==='lean' && <LeanManagement db={db} pmDb={pmDb} setPmDb={setPmDb} apiLive={apiLive} />}
        </div>
      </div>
    </>
  );
}
